import React, { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Globe, Clock, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import apiClient from '../services/api';

const TIMEZONES = [
  { code: 'GMT', name: 'Greenwich Mean Time', offset: '+0:00', group: 'GMT', display: 'GMT' },
  { code: 'IST', name: 'India Standard Time', offset: '+5:30', group: 'IST', display: 'IST' },
  { code: 'EST', name: 'Eastern Time', offset: '-5:00', group: 'Eastern', display: 'Eastern Time' },
  { code: 'EDT', name: 'Eastern Time', offset: '-4:00', group: 'Eastern', display: 'Eastern Time' },
  { code: 'CST', name: 'Central Time', offset: '-6:00', group: 'Central', display: 'Central Time' },
  { code: 'CDT', name: 'Central Time', offset: '-5:00', group: 'Central', display: 'Central Time' },
  { code: 'MST', name: 'Mountain Time', offset: '-7:00', group: 'Mountain', display: 'Mountain Time' },
  { code: 'MDT', name: 'Mountain Time', offset: '-6:00', group: 'Mountain', display: 'Mountain Time' },
  { code: 'PST', name: 'Pacific Time', offset: '-8:00', group: 'Pacific', display: 'Pacific Time' },
  { code: 'PDT', name: 'Pacific Time', offset: '-7:00', group: 'Pacific', display: 'Pacific Time' },
  { code: 'BST', name: 'British Summer Time', offset: '+1:00', group: 'UK', display: 'UK Time' },
  { code: 'AST', name: 'Atlantic Time', offset: '-4:00', group: 'Atlantic', display: 'Atlantic Time' },
  { code: 'ADT', name: 'Atlantic Time', offset: '-3:00', group: 'Atlantic', display: 'Atlantic Time' },
];

const TimezoneDisplay = ({ 
  showSelector = false, 
  compact = false,
  vaName = null,
  className = ''
}) => {
  const [timezone, setTimezone] = useState('GMT');
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Auto-detect browser timezone and map to supported timezones
  const detectBrowserTimezone = () => {
    try {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const offsetMinutes = new Date().getTimezoneOffset();
      const offsetHours = -offsetMinutes / 60;
      
      // Map common browser timezones to our supported list
      if (browserTz.includes('Kolkata') || browserTz.includes('Calcutta') || offsetHours === 5.5) {
        return 'IST';
      } else if (browserTz.includes('America/New_York') || offsetHours === -5) {
        // Check if DST is in effect (March-November)
        const month = new Date().getMonth();
        return (month >= 2 && month <= 10) ? 'EDT' : 'EST';
      } else if (browserTz.includes('America/Los_Angeles') || offsetHours === -8) {
        const month = new Date().getMonth();
        return (month >= 2 && month <= 10) ? 'PDT' : 'PST';
      } else if (browserTz.includes('Europe/London') || browserTz.includes('UTC') || offsetHours === 0) {
        return 'GMT';
      }
      
      // Default based on offset
      if (offsetHours >= 4 && offsetHours <= 6) return 'IST';
      if (offsetHours >= -6 && offsetHours <= -4) return 'EST';
      if (offsetHours >= -9 && offsetHours <= -7) return 'PST';
      
      return 'GMT';
    } catch (error) {
      return 'GMT';
    }
  };

  useEffect(() => {
    fetchUserTimezone();
  }, []);

  useEffect(() => {
    // Update current time every second for accuracy
    const updateTime = () => {
      const now = new Date();
      // Display browser's local time directly
      setCurrentTime(now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }));
    };
    
    updateTime();
    const interval = setInterval(updateTime, 1000); // Update every second
    return () => clearInterval(interval);
  }, [timezone]);

  const fetchUserTimezone = async () => {
    try {
      // VAs have their timezone stored in the database
      if (user.role === 'va') {
        const response = await apiClient.get('/attendance/user-timezone');
        setTimezone(response.data.timezone || 'GMT');
      } else {
        // For admin/client, auto-detect browser timezone
        const detectedTz = detectBrowserTimezone();
        setTimezone(detectedTz);
      }
    } catch (error) {
      // Fallback to auto-detect for all users
      const detectedTz = detectBrowserTimezone();
      setTimezone(detectedTz);
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneChange = async (newTz) => {
    try {
      await apiClient.put('/attendance/timezone', { timezone: newTz });
      setTimezone(newTz);
      toast.success(`Timezone updated to ${newTz}`);
    } catch (error) {
      toast.error('Failed to update timezone');
    }
  };

  const tzInfo = TIMEZONES.find(t => t.code === timezone) || TIMEZONES[0];

  if (loading) {
    return (
      <div className={`animate-pulse bg-[#F3F4F6] rounded h-8 w-24 ${className}`}></div>
    );
  }

  // Compact display for headers
  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Badge 
          variant="outline" 
          className="bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-1.5"
          data-testid="timezone-badge"
        >
          <Globe className="w-3 h-3" />
          <span className="font-medium">{tzInfo.display || tzInfo.group || timezone}</span>
          <span className="text-blue-500">|</span>
          <Clock className="w-3 h-3" />
          <span>{currentTime}</span>
        </Badge>
      </div>
    );
  }

  // Full display with optional selector
  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="timezone-display">
      <div className="flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 rounded-lg border border-blue-100">
        <Globe className="w-5 h-5 text-blue-500" />
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="font-medium text-blue-700">{tzInfo.display || tzInfo.group || timezone}</span>
            {vaName && (
              <span className="text-sm text-[#4B5563]">{vaName}</span>
            )}
          </div>
          <span className="text-xs text-[#4B5563]">{tzInfo.name} (UTC{tzInfo.offset})</span>
        </div>
        <div className="ml-3 pl-3 border-l border-blue-200">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4 text-blue-500" />
            <span className="font-medium text-[#0A0D0A]">{currentTime}</span>
          </div>
          <span className="text-xs text-[#4B5563]">Current Time</span>
        </div>
      </div>

    </div>
  );
};

// Badge-only version for team member cards
export const TimezoneBadge = ({ timezone = 'GMT', className = '' }) => {
  const tzInfo = TIMEZONES.find(t => t.code === timezone) || TIMEZONES[0];
  
  return (
    <Badge 
      variant="outline" 
      className={`bg-blue-50 text-blue-700 border-blue-200 ${className}`}
      data-testid="va-timezone-badge"
    >
      <Globe className="w-3 h-3 mr-1" />
      {timezone}
    </Badge>
  );
};

// Working hours indicator
export const WorkingHoursIndicator = ({ timezone = 'GMT', className = '' }) => {
  const [isWorkingHours, setIsWorkingHours] = useState(false);
  
  useEffect(() => {
    const checkWorkingHours = () => {
      const tz = TIMEZONES.find(t => t.code === timezone);
      if (!tz) return;
      
      const now = new Date();
      const offset = parseFloat(tz.offset.replace(':', '.').replace('+', ''));
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const localTime = new Date(utc + (3600000 * offset));
      
      const hour = localTime.getHours();
      const day = localTime.getDay();
      
      // Working hours: 9 AM - 6 PM, Mon-Fri
      const isWeekday = day >= 1 && day <= 5;
      const isDuringHours = hour >= 9 && hour < 18;
      
      setIsWorkingHours(isWeekday && isDuringHours);
    };
    
    checkWorkingHours();
    const interval = setInterval(checkWorkingHours, 60000);
    return () => clearInterval(interval);
  }, [timezone]);
  
  return (
    <Badge 
      className={`${isWorkingHours ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'} ${className}`}
      data-testid="working-hours-badge"
    >
      <Clock className="w-3 h-3 mr-1" />
      {isWorkingHours ? 'Working Hours' : 'Off Hours'}
    </Badge>
  );
};

export default TimezoneDisplay;
